import { useState, useEffect, useCallback } from 'react';
import cachedAPI from '../utils/cachedAPI';

/**
 * Custom hook for making cached API requests
 * @param {string} url - API endpoint URL
 * @param {Object} options - Request options
 * @param {number} cacheTTL - Cache time to live in milliseconds (default: 5 minutes)
 * @param {boolean} immediate - Whether to make the request immediately (default: true)
 * @returns {Object} { data, loading, error, refetch, clearCache }
 */
export function useCachedAPI(url, options = {}, cacheTTL = 5 * 60 * 1000, immediate = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    if (!url) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await cachedAPI.get(url, options, cacheTTL);
      setData(response.data);
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const clearCache = () => {
    cachedAPI.invalidateCache(url);
  };

  useEffect(() => {
    if (immediate && url) {
      fetchData();
    }
  }, [url, immediate, fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    clearCache,
  };
}

/**
 * Hook for cache statistics and management
 * @returns {Object} { stats, clearAll, cleanup, refresh }
 */
export function useCacheManager() {
  const [stats, setStats] = useState(null);

  const refresh = () => {
    setStats(cachedAPI.getCacheStats());
  };

  const clearAll = () => {
    cachedAPI.clearCache();
    refresh();
  };

  const cleanup = () => {
    const cleanedCount = cachedAPI.cleanupCache();
    refresh();
    return cleanedCount;
  };

  useEffect(() => {
    refresh();
  }, []);

  return {
    stats,
    clearAll,
    cleanup,
    refresh,
  };
}

export default useCachedAPI;
